import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Spinner,
  Table,
  Modal,
  SortableTh,
  PageHeader,
  ServiceAreaPill,
  EmptyValue,
  ClampText,
  FilterGrid,
  FilterField,
  ListPage,
  TableScroll,
  StatusBadge,
  RowActions,
  ACTIONS_COL_WIDTH,
  IdLink,
  confirmAction,
} from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import Toast, { ToastType } from '../../../components/Toast';
import standingTaskService, { StandingTask, StandingTaskFilters, StandingTaskFilterOptions } from '../../../services/standingTaskService';
import TaskTypeSelectionModal from '../components/modals/TaskTypeSelectionModal';
import OverheadStandingTaskModal from '../components/modals/OverheadStandingTaskModal';
import RoutineTaskModal from '../components/modals/RoutineTaskModal';
import QuantifiedTaskModal from '../components/modals/QuantifiedTaskModal';
import OverheadTaskModal from '../../overhead/components/modals/OverheadTaskModal';
import { RoutineTask, TaskSubType } from '../../../types/RoutineTask';
import { Activity } from '../../../types/Activity';
import { activityService } from '../../../services/activityService';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import FiltersCard from '../../../components/FiltersCard';
import ServerPagination from '../../../components/ServerPagination';
import { formatStatusForDisplay } from '../../../utils/statusUtils';

/** One ⋮ menu (V2-072). */
const STANDING_ACTIONS_COL_WIDTH = ACTIONS_COL_WIDTH;

const StandingTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<StandingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [showQuantifiedModal, setShowQuantifiedModal] = useState(false);
  const [showOverheadModal, setShowOverheadModal] = useState(false);
  const [editingTask, setEditingTask] = useState<StandingTask | null>(null);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: ToastType }>({
    show: false,
    message: '',
    type: 'info'
  });
  // Applied filters, search, sort and page are kept for the sign-in (V2-024, FLT-09); the search
  // box starts from the remembered search.
  const [searchTerm, setSearchTerm] = usePersistedListState('standingTasks', 'search', '');
  const [searchInput, setSearchInput] = useState(searchTerm);
  const { hasPermission } = usePermissions();
  const { isMobile } = useDeviceSize();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = usePersistedListState<string | null>('standingTasks', 'sortBy', null);
  const [sortDir, setSortDir] = usePersistedListState<'asc' | 'desc'>('standingTasks', 'sortDir', 'asc');

  // Pagination state
  const [pagination, setPagination] = usePersistedListState('standingTasks', 'pagination', {
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  // Filters state (applied to server)
  const [filters, setFilters] = usePersistedListState<StandingTaskFilters>('standingTasks', 'filters', {
    type: 'All',
    status: [],
    serviceArea: '',
    activityName: [],
    search: ''
  });

  // Pending filters (not yet applied); start from the remembered applied filters.
  const [pendingFilters, setPendingFilters] = useState<StandingTaskFilters>(filters);

  // Filter options from server
  const [filterOptions, setFilterOptions] = useState<StandingTaskFilterOptions>({
    serviceAreas: [],
    activities: [],
    statuses: [],
    types: ['All', 'Routine', 'Quantified', 'Overhead']
  });

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const options = await standingTaskService.getFilterOptions();
        setFilterOptions({
          ...options,
          types: ['All', 'Routine', 'Quantified', 'Overhead']
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  // Fetch tasks when pagination or filters change
  useEffect(() => {
    fetchTasks();
  }, [pagination.page, pagination.limit, filters, searchTerm, sortBy, sortDir]);

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      
      // Combine filters with search term
      const combinedFilters: StandingTaskFilters = {
        ...filters,
        search: searchTerm,
        ...(sortBy ? { sortBy, sortOrder: sortDir } : {})
      };

      const response = await standingTaskService.getStandingTasksPaginated(
        pagination.page,
        pagination.limit,
        combinedFilters
      );

      setTasks(response.data);
      setPagination(prev => ({
        ...prev,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNext: response.pagination.hasNext,
        hasPrevious: response.pagination.hasPrevious
      }));
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to fetch tasks');
      setTasks([]);
      setToast({
        show: true,
        message: 'Failed to fetch tasks',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowTypeSelection(true);
  };

  const handleTaskTypeSelect = (type: 'Routine' | 'Quantified' | 'Overhead') => {
    setShowTypeSelection(false);
    if (type === 'Routine') {
      setShowRoutineModal(true);
    } else if (type === 'Quantified') {
      setShowQuantifiedModal(true);
    } else if (type === 'Overhead') {
      setShowOverheadModal(true);
    }
  };

  const handleEditTask = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    setEditingTask(task);
    if (task.type === 'Routine') {
      setShowRoutineModal(true);
    } else if (task.type === 'Quantified') {
      setShowQuantifiedModal(true);
    } else if (task.type === 'Overhead') {
      setShowOverheadModal(true);
    }
  };

  const handleTaskSaved = async () => {
    setShowRoutineModal(false);
    setShowQuantifiedModal(false);
    setShowOverheadModal(false);
    setEditingTask(null);
    setToast({
      show: true,
      message: editingTask ? 'Task updated successfully' : 'Task created successfully',
      type: 'success'
    });
    // Refresh the task list
    await fetchTasks();
  };

  const handleFilterChange = (name: string, value: string | string[]) => {
    setPendingFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when applying filters
  };

  const handleClearFilters = () => {
    const clearedFilters: StandingTaskFilters = {
      type: 'All',
      status: [],
      serviceArea: '',
      activityName: [],
      search: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = () => {
    return (filters.type && filters.type !== 'All') ||
           (filters.status && filters.status.length > 0) ||
           (filters.serviceArea && filters.serviceArea !== '') ||
           (filters.activityName && filters.activityName.length > 0);
  };

  /** How many applied filters differ from their defaults - the collapsed filter bar's chip. */
  const activeFilterCount = () =>
    [
      Boolean(filters.type && filters.type !== 'All'),
      (filters.status?.length ?? 0) > 0,
      Boolean(filters.serviceArea),
      (filters.activityName?.length ?? 0) > 0,
    ].filter(Boolean).length;

  const hasUnappliedChanges = () => {
    const compareArrays = (a: string[] | undefined, b: string[] | undefined): boolean => {
      const arrA = a || [];
      const arrB = b || [];
      return arrA.length !== arrB.length || !arrA.every((v, i) => v === arrB[i]);
    };
    
    return pendingFilters.type !== filters.type ||
           compareArrays(pendingFilters.status, filters.status) ||
           pendingFilters.serviceArea !== filters.serviceArea ||
           compareArrays(pendingFilters.activityName, filters.activityName);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleModalClose = () => {
    setShowRoutineModal(false);
    setShowQuantifiedModal(false);
    setShowOverheadModal(false);
    setEditingTask(null);
  };

  // V2-072 / ACT-03: Delete is confirmed in the shared dialog, naming the item.
  const handleDeleteClick = async (task: StandingTask) => {
    const ok = await confirmAction({
      title: 'Delete Standing Task?',
      body: `${task.taskNumber ? `${task.taskNumber} – ` : ''}${task.name} is removed. This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (ok) await handleDeleteTask(task.id);
  };
  const handleDeleteTask = async (taskId: number) => {
    try {
      await standingTaskService.deleteStandingTask(taskId);
      setToast({
        show: true,
        message: 'Task deleted successfully',
        type: 'success'
      });
      // Refresh the task list
      await fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      setToast({
        show: true,
        message: 'Failed to delete task',
        type: 'error'
      });
    }
  };

  // Search lives in the filter panel header (V2-077): Enter (or the clear "x") runs it, within the
  // applied filters (V2-076).
  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const mapToRoutineTask = (task: StandingTask): RoutineTask | null => {
    if (task.type !== 'Routine') return null;
    
    return {
      id: task.id,
      taskId: task.id,
      type: 'Routine',
      description: task.description || '',
      status: task.status,
      taskSubType: (task.routineTasks?.[0]?.taskSubType as TaskSubType) || TaskSubType.Numbered_NonNumbered,
      activities: task.activities as Activity[] || [],
      labourAllocation: task.routineTasks?.[0]?.labourAllocation || 100,
      materialAllocation: task.routineTasks?.[0]?.materialAllocation || 100,
      equipmentAllocation: task.routineTasks?.[0]?.equipmentAllocation || 100,
      secondaryLabourAllocation: task.routineTasks?.[0]?.secondaryLabourAllocation || 0,
      secondaryMaterialAllocation: task.routineTasks?.[0]?.secondaryMaterialAllocation || 0,
      secondaryEquipmentAllocation: task.routineTasks?.[0]?.secondaryEquipmentAllocation || 0,
      name: task.name,
      mode: task.mode,
      taskNumber: task.taskNumber,
      createdAt: task.createdAt
    };
  };

  const mapToQuantifiedTask = (task: StandingTask) => {
    if (task.type !== 'Quantified') return null;
    
    // Get the activity ID from the activities array
    // Only set activityId if there's actually an activity
    const activityId = task.activities && task.activities.length > 0 
      ? task.activities[0].id 
      : undefined;
    
    const result: {
      id: number;
      taskNumber: string;
      name: string;
      activityId?: number;
      description: string;
    } = {
      id: task.id,
      taskNumber: task.taskNumber,
      name: task.name,
      description: task.description || ''
    };
    
    // Only include activityId if it exists
    if (activityId !== undefined) {
      result.activityId = activityId;
    }
    
    return result;
  };

  const mapToOverheadTask = (task: StandingTask): RoutineTask | null => {
    if (task.type !== 'Overhead') return null;
    
    return {
      id: task.id,
      taskId: task.id,
      type: 'Routine',
      description: task.description || '',
      status: task.status,
      taskSubType: TaskSubType.Numbered_NonNumbered,
      activities: task.activities as Activity[] || [],
      labourAllocation: 100,
      materialAllocation: 100,
      equipmentAllocation: 100,
      secondaryLabourAllocation: 0,
      secondaryMaterialAllocation: 0,
      secondaryEquipmentAllocation: 0,
      name: task.name,
      mode: task.mode || 'standing',
      taskNumber: task.taskNumber,
      createdAt: task.createdAt,
      foremanAreaId: task.foremanArea?.id
    };
  };

  if (!hasPermission('standing:tasks_view')) {
    return (
      <ListPage>
        <Alert variant="danger">You do not have permission to view this page.</Alert>
      </ListPage>
    );
  }

  const canEditTasks = hasPermission('standing:tasks_edit');
  const canDeleteTasks = hasPermission('standing:tasks_delete');
  const showActions = canEditTasks || canDeleteTasks;

  return (
    // The shared list layout (W-H5): header, filters and search keep their height; the table box
    // takes the rest and is the ONE scroll area, with a sticky header (there was none) and Task ID
    // and Actions pinned while the other columns scroll sideways.
    <ListPage className="standing-tasks-page">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />

      <PageHeader
        title="Standing Tasks"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            overflow: true,
            onClick: () => {
              setLoading(true);
              Promise.all([
                fetchTasks(),
                standingTaskService.getFilterOptions().then(options =>
                  setFilterOptions({
                    ...options,
                    types: ['All', 'Routine', 'Quantified', 'Overhead']
                  })
                )
              ]).finally(() => setLoading(false));
            },
          },
          {
            key: 'create',
            label: 'Add Task',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('standing:tasks_create'),
            onClick: handleCreateTask,
          },
        ]}
      />

      {error && (
        <Alert variant="danger" className="tw-mb-3">
          {error}
        </Alert>
      )}

      {/* Filters Card */}
      <FiltersCard
        title="Standing Task Filters"
        subtitle="Filter standing tasks by type, service area, activity, and status"
        collapsible={true}
        // FiltersCard starts collapsed on a phone and a tablet by itself (W-iP6).
        defaultCollapsed={false}
        activeFilterCount={activeFilterCount()}
        // Status is a real workflow status (Active, Planned, Cancelled; Routine also Inactive), so it
        // keeps its dropdown rather than a Display Inactive toggle (INACT-02). Nothing hides
        // standing tasks by default, so the applied count is the changed count.
        appliedFilterCount={activeFilterCount()}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Task ID, name, description, activity or service area',
          label: 'Search standing tasks',
        }}
        showClearButton={true}
        onClear={handleClearFilters}
        hasActiveFilters={Boolean(hasActiveFilters())}
        showApplyButton={true}
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges()}
      >
        <FilterGrid>
          <FilterField>
            <SearchableDropdown
              options={filterOptions.types}
              value={pendingFilters.type || 'All'}
              onChange={(value) => handleFilterChange('type', value)}
              getValue={(type) => type}
              formatOption={(type) => type}
              placeholder="All Types"
              className="tw-w-full"
              label="Task Type"
              noOptionsMessage="No types available"
            />
          </FilterField>
          <FilterField>
            {/* Single select: the list endpoint matches ONE service area name exactly
                (server/src/modules/tasks/standingTask.service.ts, buildStandingTaskWhere). */}
            <SearchableDropdown
              options={filterOptions.serviceAreas}
              value={pendingFilters.serviceArea || ''}
              onChange={(value) => handleFilterChange('serviceArea', value)}
              getValue={(area) => area.name}
              formatOption={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              options={filterOptions.activities}
              value={pendingFilters.activityName || []}
              onChange={(value) => handleFilterChange('activityName', value.map(v => String(v)))}
              getValue={(activity) => activity.activityName}
              formatOption={(activity) => `#${activity.activityNumber} - ${activity.activityName}`}
              placeholder="All Activities"
              className="tw-w-full"
              label="Activity"
              noOptionsMessage="No activities available"
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown<string>
              options={filterOptions.statuses}
              value={pendingFilters.status || []}
              onChange={(value) => handleFilterChange('status', value.map(v => String(v)))}
              getValue={(status) => status}
              formatOption={(status) => formatStatusForDisplay(status)}
              placeholder="All Statuses"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No statuses available"
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>

      <ListPage.Body>
        {loading ? (
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-py-4">
            <Spinner animation="border" className="tw-text-primary">Loading...</Spinner>
          </div>
        ) : (
          // TableScroll is the one scroll box; nothing between it and the <table> may scroll
          // (the old `responsive` wrapper did, so the header could never stick).
          <TableScroll>
            <Table hover stickyHeader>
              <thead>
                <tr>
                  <SortableTh
                    label="Task ID"
                    sortKey="taskNumber"
                    activeKey={sortBy}
                    direction={sortDir}
                    onSort={handleSort}
                    className="wrap-pin-left wrap-pin-edge tw-whitespace-nowrap"
                  />
                  <SortableTh label="Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <SortableTh label="Type" sortKey="type" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <SortableTh label="Status" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  <th>Service Area</th>
                  <SortableTh label="Created By" sortKey="createdBy" activeKey={sortBy} direction={sortDir} onSort={handleSort} />
                  {showActions && (
                    <th className="wrap-pin-right tw-text-right" style={{ width: STANDING_ACTIONS_COL_WIDTH }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan={showActions ? 7 : 6} className="tw-py-6 tw-text-center tw-text-content-muted">
                      No standing tasks found
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task.id}>
                      {/* V2-072 / ACT-01: the ID is the link that opens the record. */}
                      <td className="wrap-pin-left wrap-pin-edge tw-whitespace-nowrap">
                        {canEditTasks ? (
                          <IdLink onClick={() => handleEditTask(task.id)}>{task.taskNumber}</IdLink>
                        ) : (
                          task.taskNumber
                        )}
                      </td>
                      <td>
                        {/* Long names wrap to 2 lines (full name in the tooltip) in a column wide
                            enough to read them (W-M7). */}
                        <div className="tw-min-w-[16rem] tw-max-w-[28rem]">
                          <ClampText>{task.name}</ClampText>
                        </div>
                      </td>
                      <td className="tw-whitespace-nowrap">{task.type}</td>
                      <td className="tw-whitespace-nowrap"><StatusBadge status={task.status} /></td>
                      <td>
                        {/* V2-070 (FMT-14): a standing task with no area applies in every area, so
                            it reads "All Service Areas" rather than the empty marker. */}
                        {(task.foremanArea?.serviceArea ?? (task as any).project?.serviceArea) ? (
                          <ServiceAreaPill value={task.foremanArea?.serviceArea ?? (task as any).project?.serviceArea} />
                        ) : (
                          <span className="tw-whitespace-nowrap">All Service Areas</span>
                        )}
                      </td>
                      <td className="tw-whitespace-nowrap">
                        {task.createdBy ? `${task.createdBy.name} ${task.createdBy.lastName}` : <EmptyValue />}
                      </td>
                      {showActions && (
                        <td className="wrap-pin-right tw-whitespace-nowrap tw-text-right" style={{ width: STANDING_ACTIONS_COL_WIDTH }}>
                          {/* V2-072 / ACT-02: one ⋮ menu instead of inline Edit / Delete buttons. */}
                          <RowActions
                            actions={[
                              { kind: 'edit', onClick: () => handleEditTask(task.id), hidden: !canEditTasks },
                              { kind: 'delete', onClick: () => void handleDeleteClick(task), hidden: !canDeleteTasks },
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </ListPage.Body>

      {/* Server-side pagination */}
      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-2"
      />

      <TaskTypeSelectionModal
        show={showTypeSelection}
        onClose={() => setShowTypeSelection(false)}
        onSelect={handleTaskTypeSelect}
      />

      <RoutineTaskModal
        open={showRoutineModal}
        task={editingTask ? mapToRoutineTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

      <QuantifiedTaskModal
        open={showQuantifiedModal}
        task={editingTask ? mapToQuantifiedTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

      <OverheadTaskModal
        open={showOverheadModal}
        task={editingTask ? mapToOverheadTask(editingTask) : null}
        onCancel={handleModalClose}
        onSuccess={handleTaskSaved}
      />

    </ListPage>
  );
};

export default StandingTasksPage; 